/**
 * GOTY auto-fill picker sheet (G-07) — NEW UI, no prototype to be faithful to.
 *
 * It opens when `steamFill` gets a Steam library back that Steam refuses to
 * date. That is not an edge case: `rtime_last_played` is disclosed only to the
 * account that owns the Web API key, so for a crew sharing one key it is every
 * member except the key's owner. The old behaviour was to toast "NO 2026 STEAM
 * PLAYTIME TO PULL IN" at somebody with thousands of hours on record.
 *
 * So the sheet has two jobs, in this order:
 *   1. Say plainly why we could not just do it. Every line of `v.why` is a count
 *      the server actually measured, not a characterisation.
 *   2. Give them a way through anyway — the fallback signals' findings, ticked
 *      where we OBSERVED something and left unticked where we guessed, plus a
 *      search over their whole owned library for whatever we missed.
 *
 * Assembled from the existing overlay vocabulary (`Sheet` + `SearchBox`), and
 * the toggle idiom is the app's usual one: a bordered row whose border, text and
 * `✓` flip to the accent together. There is no checkbox anywhere in this app and
 * this is not the place to introduce one.
 */

import { SearchBox } from '../components/SearchBox.tsx';
import { Sheet } from '../components/Sheet.tsx';
import { useQd } from '../store/index.ts';
import { gotyPickVM } from '../store/selectors.ts';

const MONO = 'var(--gt-font-mono)';

interface Row {
  key: string;
  appid: number;
  name: string;
  meta: string;
  on: boolean;
  mark: string;
  bd: string;
  clr: string;
  metaClr: string;
}

export default function GotyPickSheet(): JSX.Element {
  const s = useQd();
  const v = gotyPickVM(s);

  const row = (r: Row): JSX.Element => (
    <div
      key={r.key}
      onClick={() => s.toggleGotyPick(r.appid)}
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 10,
        border: '1px solid ' + r.bd,
        background: 'var(--gt-tile)',
        padding: '10px 11px',
        // 44px floor for a touch target (D3 mobile rule); `border-box` so the
        // border does not push it over and reflow the list.
        minHeight: 44,
        boxSizing: 'border-box',
        cursor: 'pointer',
      }}
    >
      <div
        style={{
          fontFamily: MONO,
          fontSize: 11,
          width: 14,
          flex: 'none',
          color: r.on ? 'var(--acc)' : 'var(--gt-border-3)',
        }}
      >
        {r.on ? r.mark : '·'}
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 4, flex: 1, minWidth: 0 }}>
        <div style={{ fontWeight: 600, fontSize: '12.5px', color: r.clr }}>{r.name}</div>
        <div
          style={{
            fontFamily: MONO,
            fontSize: '8.5px',
            letterSpacing: '.06em',
            color: r.metaClr,
          }}
        >
          {r.meta}
        </div>
      </div>
    </div>
  );

  return (
    <Sheet
      label="Goty pick sheet"
      maxHeight="90vh"
      hs
      onClose={s.closeGotyPick}
      header={{ title: v.title, titleClr: 'var(--gt-text)', titleFs: '8.5px', note: v.note }}
    >
      <div style={{ display: 'flex', flexDirection: 'column', gap: 11, padding: '13px 14px 18px' }}>
        {v.why.map((line, i) => (
          <div
            key={'why' + i}
            style={{ fontFamily: MONO, fontSize: 9, lineHeight: 1.7, color: 'var(--gt-muted)' }}
          >
            {line}
          </div>
        ))}

        {v.hasRows ? (
          v.rows.map(row)
        ) : (
          <div style={{ fontFamily: MONO, fontSize: 9, lineHeight: 1.7, color: 'var(--gt-muted-2)' }}>
            {v.emptyTxt}
          </div>
        )}

        {v.hasGuess ? (
          <div
            style={{
              fontFamily: MONO,
              fontSize: '8.5px',
              letterSpacing: '.03em',
              lineHeight: 1.7,
              color: 'var(--gt-ghost-2)',
            }}
          >
            {v.guessTxt}
          </div>
        ) : null}

        <div
          style={{
            fontFamily: MONO,
            fontSize: '8.5px',
            letterSpacing: '.06em',
            color: 'var(--gt-faint)',
            borderTop: '1px solid var(--gt-line)',
            paddingTop: 12,
          }}
        >
          MISSING SOMETHING? SEARCH YOUR LIBRARY
        </div>

        <SearchBox
          value={v.q}
          onChange={(e) => s.setGotyPickQ(e.target.value)}
          placeholder={v.searchPlaceholder}
          w="100%"
          ifs={11}
          pad="13px 12px"
        />

        {v.libRows.map(row)}

        {v.libNoHit ? (
          <div style={{ fontFamily: MONO, fontSize: 9, lineHeight: 1.7, color: 'var(--gt-muted-2)' }}>
            {v.libNoHitTxt}
          </div>
        ) : null}

        <div
          onClick={() => {
            // The button is a plain `div`, so there is no `disabled` doing this
            // for us — the guard has to be here as well as in the action.
            if (v.canConfirm) void s.gotyPickConfirm();
          }}
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            border: '1px solid ' + v.confirmBd,
            color: v.confirmClr,
            background: 'var(--gt-panel)',
            fontFamily: MONO,
            fontSize: 10,
            letterSpacing: '.06em',
            height: 46,
            boxSizing: 'border-box',
            cursor: v.busy ? 'progress' : v.canConfirm ? 'pointer' : 'default',
          }}
        >
          {v.confirmLbl}
        </div>
      </div>
    </Sheet>
  );
}
